import classNames from 'classnames';
import type React from 'react';
import { type KeyboardEvent, forwardRef, useRef, useState } from 'react';
import { Spacer } from '../../Spacer';
import { Tag } from '../../Tag/Tag';
import { TextBadge } from '../../Typography';

export interface InputTagsProps {
  errorIndexes: string[];
  onNewTag: (tags: string[]) => void;
  onRemoveTag?: (tags: string[]) => void;
  name: string;
  tags: string[];
  placeholder: string;
  success?: boolean;
  maxHeight?: number;
}

export const InputTags = forwardRef(
  (
    {
      success,
      errorIndexes,
      onNewTag,
      onRemoveTag,
      name,
      tags,
      placeholder,
      maxHeight,
    }: InputTagsProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState<string>('');
    const focusInput = () => {
      inputRef?.current?.focus();
      window.setTimeout(() =>
        containerRef?.current?.scrollTo(0, containerRef?.current.scrollHeight)
      );
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Backspace' &&
        event.currentTarget.value.length === 0 &&
        tags.length > 0
      ) {
        removeTag(tags[tags.length - 1]);
      }
      if (
        event.key === 'Enter' ||
        event.code === 'Space' ||
        event.key === 'Tab'
      ) {
        event.preventDefault();
        if (event.currentTarget.value.length > 0) {
          onNewTag?.([...tags, event.currentTarget.value]);
          setInputValue('');
          focusInput();
        }
      }
    };
    const onClickOut = () => {
      if (inputValue?.length > 0) {
        onNewTag?.([...tags, inputValue]);
        setInputValue('');
        focusInput();
      }
    };
    const removeTag = (tag) => {
      onRemoveTag?.(tags.filter((val) => val !== tag));
    };
    return (
      <div
        ref={ref}
        className={classNames(
          'border relative w-full rounded-md flex justify-center items-center min-h-ds-9 bg-white px-1',
          { 'border-ds-primary': success }
        )}
      >
        <div
          ref={containerRef}
          style={{
            maxHeight: maxHeight ? `${maxHeight}px` : 'unset',
          }}
          className="w-full flex flex-wrap justify-start items-center overflow-y-auto overflow-x-hidden gap-1"
        >
          {tags.map((tag, index) => (
            <div
              className="w-max"
              key={`${tag}${index}`}
            >
              <Tag
                danger={!!errorIndexes.find((ind) => index.toString() === ind)}
                onClose={() => removeTag(tag)}
              >
                <Spacer amount={0.375} />
                <div className="max-w-ds-178 flex items-center">
                  <TextBadge
                    ellipsis
                    label={tag}
                  />
                </div>
              </Tag>
            </div>
          ))}
          <textarea
            onKeyDown={handleKeyDown}
            onBlur={onClickOut}
            autoComplete="off"
            ref={inputRef}
            name={name}
            rows={1}
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
            }}
            placeholder={placeholder}
            id={name}
            className="placeholder-ds-natural-300 py-0 text-sm outline-none border-0 focus:outline-none focus:ring-0 focus:border-0 font-ds-inter my-1.5 text-ds-natural-600 bg-white border-transparent flex min-w-42 flex-1 resize-none"
          />
        </div>
      </div>
    );
  }
);

InputTags.displayName = 'InputTags';
